'use client'

import { type ComponentProps, type FC, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  type GkAirProxyServiceWebResponseRecommendationsValue,
  type GkAirRecommendedProductAttributesWeb,
} from '@redteclab/api/clients/crosssellers-proxy-service'

import { useRecommendationIntersection } from '../../../cross-sell'
import { useExperimentsPageType } from '../../../experiments/components/ExperimentsPageTypeContext'
import {
  EXPERIMENTS_TRACK_EVENT_RECOMMENDATION_PRODUCT_CLICK_HOMEPAGE,
  experimentsTrackEvent,
} from '../../../experiments/eventTracking'
import { EXPERIMENT_PAGE_TYPE_HOMEPAGE } from '../../../experiments/ExperimentPageType'
import {
  gtmDataLayerPushRecommendationTracking,
  type RecommendationServiceType,
  RecommendationWidgetContainer,
  RecommendationWidgetMoreLink,
  RecommendationWidgetProvider,
  RecommendationWidgetTitle,
  useIsRecommendationWidgetIdValid,
  useSwrFetchRecommendationService,
} from '../../../recommendation'

type StoryblokRecommendationWidgetRendererProps = {
  cid?: string
  containerClassName?: string
  editableProps?: ComponentProps<'div'>
  renderProducts: (params: {
    onGtmDataLayerPush: (
      widgetProductId: string,
      trackingToken?: GkAirRecommendedProductAttributesWeb['trackingtoken'],
    ) => void
    widgetData?: GkAirProxyServiceWebResponseRecommendationsValue
    widgetId: string
  }) => ReactNode
  serviceId: RecommendationServiceType
  widgetId: string
}

export const StoryblokRecommendationWidgetRenderer: FC<
  StoryblokRecommendationWidgetRendererProps
> = ({
  cid,
  containerClassName,
  editableProps,
  renderProducts,
  serviceId,
  widgetId,
}) => {
  const pageType = useExperimentsPageType()

  const { handleIntersection, intersected } = useRecommendationIntersection()

  const { data, error } = useSwrFetchRecommendationService(
    intersected ? { cid, pid: '', serviceId } : undefined,
  )

  const isRecommendationWidgetValid = useIsRecommendationWidgetIdValid(
    widgetId,
    serviceId,
  )
  const recommendation = data?.recommendations
  const widgetData = isRecommendationWidgetValid
    ? recommendation?.[widgetId]
    : undefined

  if (
    !isRecommendationWidgetValid ||
    error ||
    (recommendation && (!widgetData || widgetData.content.length === 0))
  ) {
    return null
  }

  const onGtmDataLayerPush = (
    widgetProductId: string,
    trackingToken?: GkAirRecommendedProductAttributesWeb['trackingtoken'],
  ): void => {
    gtmDataLayerPushRecommendationTracking({
      trackingToken,
      widgetId,
      widgetProductId,
    })

    if (pageType === EXPERIMENT_PAGE_TYPE_HOMEPAGE) {
      experimentsTrackEvent(
        EXPERIMENTS_TRACK_EVENT_RECOMMENDATION_PRODUCT_CLICK_HOMEPAGE,
      )
    }
  }

  return (
    <RecommendationWidgetProvider
      serviceId={serviceId}
      widgetData={widgetData}
      widgetId={widgetId}
    >
      <RecommendationWidgetContainer
        className={containerClassName}
        onIntersect={handleIntersection}
        widgetData={data}
        widgetId={widgetId}
        {...editableProps}
      >
        <div className="mb-4 flex flex-col items-start justify-between gap-1 tablet:flex-row tablet:items-center">
          <RecommendationWidgetTitle className="heading heading--size-l" />
          <RecommendationWidgetMoreLink>
            <FormattedMessage id="general.label.ShowAllProducts" />
          </RecommendationWidgetMoreLink>
        </div>

        {renderProducts({
          onGtmDataLayerPush,
          widgetData,
          widgetId,
        })}
      </RecommendationWidgetContainer>
    </RecommendationWidgetProvider>
  )
}
